import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';
import { getFairground, preloadFairground } from '../fairground-assets.js';
import { PyroLayer, GROUND_Y as PYRO_GROUND_Y } from '../particle-layers/pyro-layer.js';

// Shared-rig contract: forbidden. The fair's bulbs are the only light.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'fairground',
  family: 'photographic',
  danceability: 0.72,
  energyLevel: 0.66,
  bpmMin: 95,
  bpmMax: 135,
  movement: 0.60,
  tonality: 0.80,
  prefers: { gridStrength: [0.55, 1.0] },
  requiresTier: 0,
  enabled: true
};

// Fairground: a fair at night from two identical photographs - bulbs off,
// bulbs on. Seven hundred bulbs, each on its own dimmer: marquee chases,
// a big wheel that seems to turn, a carousel that twinkles. Pop, disco,
// house, electro-swing, nu-disco.
//
// THE RENDER IS NEON ALLEY'S: per pixel, colour = off + diff x intensity
// (bulb of the pixel) x tint(bulb), with the lossless id map of
// tools/build-fairground.mjs saying which bulb a pixel belongs to and how
// much of its halo it carries. What is new is that the intensity is not a
// table written by the CPU: it is COMPUTED IN THE SHADER from the bulb's
// place - its group, its angle and radius about the wheel's hub, its index
// along its line - and the rhythm's uniforms (the chase step, the beat,
// the bar phase, the pattern). The per-bulb data (one texel per bulb) is
// uploaded once, at load. The filament's inertia (40 ms up, 180 ms down)
// is exact without state: a pattern is a function of (bulb, step), so the
// shader evaluates it for this step and the previous one and blends by
// the time since the step with the rising or falling constant.
//
// THE CHASE IN THIRDS is the signature. On a fairground marquee one bulb
// in three is lit and the pattern advances a notch: the eye sees the light
// RUN along the line. lit = (index + step) mod 3 == 0, the step advanced
// every quarter beat. By angle on the wheel's rim it makes the wheel TURN
// while the photograph stands still.
//
// PATTERNS BY GROUP, changed on the phrases: the wheel SPIN / SWEEP (a
// spoke a bar) / RINGS (one per beat from the hub) / EQ (each spoke a
// column); the carousel a soft random twinkle plus a chase round the
// canopy; the stalls alternate arches, one stand per beat, with the chase;
// the strings a wave down the alley once a bar. Colours: incandescent
// white, or ALTERNATE by phrase - every other bulb in a palette colour.
// Drop: everything out for a beat, then every bulb at once in white at
// 1.3, then a fast SPIN (a notch every eighth) for four bars, and a few
// fireworks of PyroLayer behind the wheel.

// --- rhythm ------------------------------------------------------------------
const CHASE_PER_BEAT = 4;        // notches per beat
const CHASE_FAST_PER_BEAT = 8;   // after the drop
const DROP_DARK_BEATS = 1;
const DROP_FLASH = 1.3;
const DROP_FAST_BARS = 4;
const HEAT_RATE = 1 / 0.040;     // the filament: 40 ms up
const COOL_RATE = 1 / 0.180;     // 180 ms down
const FALLBACK_BPM = 118;
const PAT_SPIN = 0, PAT_SWEEP = 1, PAT_RINGS = 2, PAT_EQ = 3;
const WHEEL_PATTERNS = [PAT_SPIN, PAT_SWEEP, PAT_RINGS, PAT_EQ];
const WHEEL_NAMES = { spin: PAT_SPIN, sweep: PAT_SWEEP, rings: PAT_RINGS, eq: PAT_EQ };
const RIM_R = 0.85;              // r above this is the rim
const RIM_SECTORS = 96;          // the rim's chase, in angular sectors: about one bulb each
const SPOKES = 16;
const RINGS = 6;
const BASE_DIM = 0.08;           // an "off" bulb in a pattern still glows a little
const STALL_DIM = 0.35;          // the stands not on their beat
const TWINKLE_RATE = [1.5, 6.0]; // bands.high: carousel glyph changes per second
const COLOR_SAT = 0.85;
const PALETTE_SHARE = 0.5;       // the ALTERNATE mode: every other bulb
const HALO_MEAN = 0.6;           // a far halo follows the mean intensity this much
// fair-off.jpeg is not dark: the generator left every bulb glowing at
// about two fifths of fair-on, so "off + diff x I" at I 0 still showed a
// lit fair and a chase in thirds read as every bulb on. Part of the
// residual is taken out in proportion: colour = off + diff x ((1 + R) I -
// R), fair-on at I 1, darker than fair-off by R x diff at I 0, and equal
// to fair-off at I 0.29 - so a stand off its beat (0.35) or a soft
// twinkle still glows as the photograph does, and only the chase's dark
// thirds and the drop's black go under it. (At R 0.65 anything under 0.4
// went darker than the photograph and the carousel's twinkle vanished.)
const OFF_RESIDUAL = 0.40;

// --- the picture ---------------------------------------------------------------
const BULBS_W = 1024;            // the data texture: one texel per bulb id
const SAFE_FLOOR = 0.55;
const FOG_TOP = 0.42;            // of the height: the ground haze reaches up to here
const FOG_GAIN = 0.10;
const BOKEH = 7;
const TIER = {
  0: { fog: false, bokeh: 0, pyro: 0 },
  1: { fog: true, bokeh: 4, pyro: 500 },
  2: { fog: true, bokeh: BOKEH, pyro: 900 }
};
const DEFAULT_TIER = 2;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function srgbToLinear(c) { return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }

// =============================================================================
// SHADERS
// =============================================================================

const VERTEX = `
  varying vec2 vUv;
  varying vec4 vClip;
  void main() {
    vUv = uv;
    vec4 clip = vec4(position.xy, 0.0, 1.0);
    vClip = clip;
    gl_Position = clip;
  }
`;

// THE BULB PASS: one fragment per bulb id (a 1024 x 1 target), the whole
// pattern logic once per bulb and not once per pixel. Measured on the Pi
// with the logic in the display pass: 40 ms at tier 1; a full-screen quad
// cannot afford four pattern evaluations and two hashes per pixel. The
// display pass then reads one texel per pixel. Output: R the intensity
// over 2 (up to 1.3), GBA the tint.
const BULB_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uBulbs;       // one texel per bulb: group, t (angle on the wheel), r (radius) or line, index
  uniform float uTime;            // wrapped on the CPU
  uniform float uStep;            // the chase notch (integer)
  uniform float uStepAge;         // seconds since the notch
  uniform float uDir;             // +1 / -1
  uniform float uBeatIdx;         // integer beats
  uniform float uBeatAge;
  uniform float uBeatPhase;
  uniform float uBarPhase;
  uniform float uWheelPat;
  uniform float uWheelPatPrev;
  uniform float uPatMix;          // 0 just switched .. 1 arrived
  uniform vec4 uEq;               // four band levels for the spokes
  uniform float uTwinkle;         // carousel: changes per second
  uniform float uGlobal;          // bands.low
  uniform float uDropDark;        // 1: everything out
  uniform float uDropFlash;       // the white, decaying
  uniform float uPause;           // 1: every bulb at half, still
  uniform vec3 uColorA;           // the ALTERNATE mode's colours, linear
  uniform vec3 uColorB;
  uniform float uAlternate;       // 0..1
  uniform float uStallCount;
  uniform float uFog;
  uniform float uBokeh;
  uniform float uSolo;            // -1, or one bulb id
  varying vec2 vUv;
  float hash21(vec2 p) {
    p = fract(p * vec2(0.3183, 0.3679)) + vec2(0.71, 0.113);
    p += dot(p, p.yx + 1.9);
    return fract(p.x * p.y * 7.31);
  }
  float chase3(float idx, float stp) { return step(abs(mod(idx + stp, 3.0)), 0.5); }
  // the wheel's pattern at a given step, for one bulb
  float wheelAt(float pat, vec4 b, float stp) {
    float a = b.y, r = b.z;
    if (pat < 0.5) {
      // SPIN: thirds by angular sector on the rim, by radius along the spokes
      float key = r > ${RIM_R.toFixed(2)} ? floor(a * ${RIM_SECTORS.toFixed(1)}) : floor(r * 10.0) + floor(a * ${SPOKES.toFixed(1)});
      return chase3(key, stp * uDir);
    } else if (pat < 1.5) {
      // SWEEP: a lit spoke, one turn a bar
      float d = abs(fract(a - uBarPhase * uDir + 0.5) - 0.5);
      return smoothstep(0.045, 0.0, d) * 0.9 + 0.1 * step(r, 0.06);
    } else if (pat < 2.5) {
      // RINGS: one per beat, from the hub out
      float ring = floor(min(r, 0.999) * ${RINGS.toFixed(1)});
      return step(abs(mod(ring - uBeatIdx, ${RINGS.toFixed(1)})), 0.5);
    }
    // EQ: each spoke a column from the hub, the rim always on at a third
    float spoke = floor(a * ${SPOKES.toFixed(1)});
    float lv = mix(mix(uEq.x, uEq.y, step(1.5, mod(spoke, 4.0))), mix(uEq.z, uEq.w, step(2.5, mod(spoke, 4.0))), step(0.5, mod(spoke, 2.0)));
    lv *= 0.75 + 0.5 * hash21(vec2(spoke / 16.0, floor(uBeatIdx * 0.25) / 97.0));
    return r > ${RIM_R.toFixed(2)} ? 0.35 : step(r, lv);
  }
  float carouselAt(vec4 b, float stp) {
    // a soft random twinkle (changing uTwinkle times a second) plus the chase round the canopy
    float k = floor(uTime * uTwinkle + hash21(vec2(b.w / 977.0, b.z / 53.0)) * 7.0);
    float tw = 0.35 + 0.65 * hash21(vec2(b.w / 991.0 + 0.1, mod(k, 251.0) / 251.0));
    return mix(tw, chase3(b.w, stp), 0.5);
  }
  float stallsAt(vec4 b, float stp) {
    float onBeat = step(abs(mod(b.z - uBeatIdx, max(uStallCount, 1.0))), 0.5);
    return chase3(b.w, stp) * mix(${STALL_DIM.toFixed(2)}, 1.0, onBeat);
  }
  float stringsAt(vec4 b, float stp) {
    float w = exp(-pow((b.y - uBarPhase) * 6.0, 2.0));
    return chase3(b.w, stp) * (0.6 + 0.4 * w) + 0.35 * w;
  }
  float patternAt(vec4 b, float stp, float wheelPat) {
    float g = b.x;
    if (g < 1.5) return wheelAt(wheelPat, b, stp);
    if (g < 2.5) return carouselAt(b, stp);
    if (g < 3.5) return stallsAt(b, stp);
    return stringsAt(b, stp);
  }
  void main() {
    float id = floor(vUv.x * ${BULBS_W}.0);
    vec4 b = texture2D(uBulbs, vec2((id + 0.5) / ${BULBS_W}.0, 0.5));   // group, t, r|line, idx
    // the pattern now and at the previous notch, blended by the filament
    float now = patternAt(b, uStep, uWheelPat);
    float prev = patternAt(b, uStep - 1.0, uWheelPat);
    if (uPatMix < 0.999 && b.x < 1.5) { now = mix(patternAt(b, uStep, uWheelPatPrev), now, uPatMix); prev = mix(patternAt(b, uStep - 1.0, uWheelPatPrev), prev, uPatMix); }
    float rate = now > prev ? ${HEAT_RATE.toFixed(2)} : ${COOL_RATE.toFixed(2)};
    float I = mix(prev, now, 1.0 - exp(-uStepAge * rate));
    I = mix(${BASE_DIM.toFixed(2)}, 1.0, I);
    I *= 0.75 + 0.45 * uGlobal;
    // the drop: dark, then the white at 1.3 over everything
    I *= 1.0 - uDropDark;
    I = max(I, uDropFlash * ${DROP_FLASH.toFixed(2)});
    I = mix(I, 0.5, uPause);
    if (uSolo >= 0.0) I = abs(id - uSolo) < 0.5 ? 1.0 : 0.0;
    // the tint: white, or every other bulb in a palette colour
    float odd = mod(b.w, 2.0);
    vec3 tint = mix(vec3(1.0), mix(uColorA, uColorB, odd), uAlternate * step(0.5, mod(floor(b.w * 0.5) + odd, 2.0)) * (1.0 - uDropFlash));
    gl_FragColor = vec4(I * 0.5, tint);
  }
`;

// THE DISPLAY: four reads per pixel - the id map, the bulb table, the two
// photographs - and the halos' arithmetic.
const FRAGMENT = `
  precision mediump float;
  uniform sampler2D uOff;
  uniform sampler2D uDiff;
  uniform highp sampler2D uIds;
  uniform sampler2D uBulbs;       // the build's table, for the debug colours
  uniform sampler2D uIntens;      // the bulb pass: R intensity / 2, GBA tint
  uniform vec2 uUvScale;
  uniform vec2 uUvOffset;
  uniform float uTime;
  uniform float uFog;
  uniform float uBokeh;
  uniform float uIdDebug;         // 1: groups in colour
  uniform float uMean;            // the mean intensity, for the far halos
  uniform float uDropDark;
  uniform vec4 uFireflies[${BOKEH}];   // x, y, glow, unused
  varying vec2 vUv;
  varying vec4 vClip;
${SAFE_ZONE_VERTEX_CHUNK}
  void main() {
    vec2 uv = vUv * uUvScale + uUvOffset;
    highp vec4 idc = texture2D(uIds, uv);
    highp float id = floor(idc.r * 255.0 + 0.5) * 256.0 + floor(idc.g * 255.0 + 0.5);
    float w = idc.b;
    vec3 off = texture2D(uOff, uv).rgb;
    vec3 diff = texture2D(uDiff, uv).rgb;
    if (uIdDebug > 0.5) {
      vec4 b = texture2D(uBulbs, vec2((id + 0.5) / ${BULBS_W}.0, 0.5));
      vec3 c = b.x < 1.5 ? vec3(1.0, 0.2, 0.2) : (b.x < 2.5 ? vec3(0.2, 1.0, 0.3) : (b.x < 3.5 ? vec3(1.0, 0.6, 0.1) : vec3(0.3, 0.5, 1.0)));
      if (b.x < 1.5) c = mix(c, vec3(1.0, 1.0, 0.2), step(${RIM_R.toFixed(2)}, b.z));
      gl_FragColor = vec4(off * 0.4 + diff * c * (0.3 + 0.7 * w), 1.0);
      return;
    }
    vec4 bi = texture2D(uIntens, vec2((id + 0.5) / ${BULBS_W}.0, 0.5));
    float I = bi.r * 2.0;
    vec3 tint = bi.gba;
    // a far halo follows the mean more than one bulb
    float Ih = mix(uMean, I, max(w, 1.0 - ${HALO_MEAN.toFixed(2)}));
    float Ip = w > 0.999 ? I : Ih;
    // the drop's beat of dark takes the whole residual out: the bulbs go black
    vec3 light = diff * (Ip * ${(1 + OFF_RESIDUAL).toFixed(2)} - ${OFF_RESIDUAL.toFixed(2)} - 0.45 * uDropDark) * tint;
    light *= 1.0 + 0.25 * step(0.999, w) * step(0.9, Ip);   // a lit filament's core, over the bloom's threshold
    vec3 col = max(off + light, 0.0);
    light = max(light, 0.0);
    // the ground haze catching the halos
    if (uFog > 0.5) {
      float band = 1.0 - smoothstep(0.0, ${FOG_TOP.toFixed(2)}, uv.y);
      float lum = dot(light, vec3(0.2126, 0.7152, 0.0722));
      col += vec3(0.75, 0.8, 1.0) * (${FOG_GAIN.toFixed(2)} * band * (0.35 * uMean + lum) * (0.8 + 0.2 * sin(uv.x * 9.0 + uTime * 0.3)));
    }
    // bokeh fireflies drifting past the lens: positions and glow from the
    // CPU (a distance per firefly per pixel, nothing else - the sines per
    // pixel cost the Pi a third of a tier-1 frame)
    // Unrolled with constant indices: a loop over a uniform array with a
    // dynamic index and a break cost the Pi 20 ms a frame at tier 2; the
    // fireflies beyond the tier's count have a glow of 0.
    if (uBokeh > 0.5) {
      vec2 pa = vec2(vUv.x * uAspect, vUv.y);
      float g = 0.0;
${Array.from({ length: BOKEH }, (_, i) => `      g += uFireflies[${i}].z * smoothstep(0.045, 0.02, distance(pa, vec2(uFireflies[${i}].x * uAspect, uFireflies[${i}].y)));`).join('\n')}
      col += vec3(1.0, 0.85, 0.6) * g;
    }
    float att = safeZoneAttenuation(vClip);
    att = mix(${SAFE_FLOOR.toFixed(2)}, 1.0, clamp((att - 0.32) / 0.68, 0.0, 1.0));
    gl_FragColor = vec4(col * att, 1.0);
  }
`;

// =============================================================================

const MATERIALS = new Map();
function materialFor() {
  let m = MATERIALS.get('q');
  if (m) return m;
  const uniforms = Object.assign({
    uOff: { value: null }, uDiff: { value: null }, uIds: { value: null }, uBulbs: { value: null },
    uUvScale: { value: new THREE.Vector2(1, 1) }, uUvOffset: { value: new THREE.Vector2(0, 0) },
    uTime: { value: 0 }, uStep: { value: 0 }, uStepAge: { value: 1 }, uDir: { value: 1 }, uBeatIdx: { value: 0 }, uBeatAge: { value: 1 }, uBeatPhase: { value: 0 }, uBarPhase: { value: 0 },
    uWheelPat: { value: 0 }, uWheelPatPrev: { value: 0 }, uPatMix: { value: 1 }, uEq: { value: new THREE.Vector4(0.5, 0.5, 0.5, 0.5) },
    uTwinkle: { value: 3 }, uGlobal: { value: 0.5 }, uDropDark: { value: 0 }, uDropFlash: { value: 0 }, uPause: { value: 0 },
    uColorA: { value: new THREE.Vector3(1, 0.1, 0.1) }, uColorB: { value: new THREE.Vector3(1, 1, 1) }, uAlternate: { value: 0 },
    uStallCount: { value: 3 }, uFog: { value: 1 }, uBokeh: { value: BOKEH }, uIdDebug: { value: 0 }, uSolo: { value: -1 }, uMean: { value: 0.4 }, uIntens: { value: null }, uFireflies: { value: Array.from({ length: BOKEH }, () => new THREE.Vector4(0, 0, 0, 0)) }
  }, {
    uAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
  });
  const material = new THREE.ShaderMaterial({ uniforms, vertexShader: VERTEX, fragmentShader: FRAGMENT, depthTest: false, depthWrite: false });
  const bulbMaterial = new THREE.ShaderMaterial({ uniforms, vertexShader: VERTEX, fragmentShader: BULB_FRAGMENT, depthTest: false, depthWrite: false });
  m = { uniforms, material, bulbMaterial };
  MATERIALS.set('q', m);
  return m;
}

export class FairgroundVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._layout = TIER[this._tier];
    this._idDebug = urlParam('fairid') === '1';
    const solo = parseInt(urlParam('fairbulb'), 10);
    this._solo = Number.isFinite(solo) ? solo : -1;
    const wp = urlParam('fairwheel');
    this._forcedWheel = wp != null && WHEEL_NAMES[wp] != null ? WHEEL_NAMES[wp] : null;
    this._forceDrop = urlParam('fairdrop') === '1';
    // bench switches
    this._noPyro = urlParam('fairnopyro') === '1';
    this._noBokeh = urlParam('fairnobokeh') === '1';
    this._noFog = urlParam('fairnofog') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    const assets = getFairground();
    this._assets = assets;
    if (!assets) {
      const self = this;
      preloadFairground().then(function (a) {
        if (!a || !self._scene || self._assets) return;
        const s = self._scene, c = self._camera;
        self.dispose();
        self.init(s, c);
      });
    }
    const data = assets ? assets.data : { count: 0, size: [1376, 768], bulbs: [], lines: {}, hub: [0.85, 0.17] };
    this._count = data.count;
    this._imageAspect = data.size[0] / data.size[1];
    this._stallCount = Math.max(1, (data.lines && data.lines.stalls) || 3);
    this._hub = data.hub;
    // the bulb table: one texel per id - group, t (angle on the wheel, position
    // along a line elsewhere), r (wheel) or line (others), index along the line
    this._bulbsData = new Float32Array(BULBS_W * 4);
    for (const b of data.bulbs) {
      if (b.id >= BULBS_W) continue;
      const o = b.id * 4;
      this._bulbsData[o] = b.g;
      this._bulbsData[o + 1] = b.g === 1 ? b.a : b.t;
      this._bulbsData[o + 2] = b.g === 1 ? b.r : b.line;
      this._bulbsData[o + 3] = b.idx;
    }
    this._bulbsTex = new THREE.DataTexture(this._bulbsData, BULBS_W, 1, THREE.RGBAFormat, THREE.FloatType);
    this._bulbsTex.minFilter = this._bulbsTex.magFilter = THREE.NearestFilter;
    this._bulbsTex.needsUpdate = true;

    this._m = materialFor();
    this._u = this._m.uniforms;
    this._resetUniforms();
    const u = this._u;
    if (assets) { u.uOff.value = assets.off; u.uDiff.value = assets.diff; u.uIds.value = assets.ids; }
    u.uBulbs.value = this._bulbsTex;
    u.uStallCount.value = this._stallCount;
    u.uFog.value = this._layout.fog && !this._noFog ? 1 : 0;
    u.uBokeh.value = this._noBokeh ? 0 : this._layout.bokeh;
    u.uIdDebug.value = this._idDebug ? 1 : 0;
    u.uSolo.value = this._solo;

    // the bulb pass's target and its quad
    this._intens = new THREE.WebGLRenderTarget(BULBS_W, 1, { type: THREE.UnsignedByteType, format: THREE.RGBAFormat, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, depthBuffer: false, stencilBuffer: false, generateMipmaps: false });
    u.uIntens.value = this._intens.texture;
    this._ortho = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 10);
    this._ortho.position.z = 1;
    this._bulbScene = new THREE.Scene();
    this._bulbQuad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this._m.bulbMaterial);
    this._bulbQuad.frustumCulled = false;
    this._bulbScene.add(this._bulbQuad);

    this._quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this._m.material);
    this._quad.frustumCulled = false;
    this._quad.renderOrder = -1000;
    this._quad.visible = !!assets;
    const self = this;
    // THE BULB PASS RUNS HERE, just before the picture draws.
    this._quad.onBeforeRender = function (renderer) {
      renderer.getDrawingBufferSize(_size);
      self._reframe(_size.x, _size.y);
      if (self._pyro) self._pyro.setResolution(_size.x, _size.y);
      const prev = renderer.getRenderTarget();
      renderer.setRenderTarget(self._intens);
      renderer.render(self._bulbScene, self._ortho);
      renderer.setRenderTarget(prev);
    };
    this._scene.add(this._quad);

    // the fireworks behind the wheel, on the drop
    this._pyro = null;
    if (this._layout.pyro && assets && !this._noPyro) {
      this._pyro = new PyroLayer({ max: this._layout.pyro, trails: false, smoke: false, reflection: false, crackle: true, flames: false, linewidth: 1.4, space: 'clip' });
      this._pyro.init(this._scene);
      this._pyroQueue = [];
    }

    this._time = 0;
    this._paused = false;
    this._step = 0; this._stepAge = 1; this._stepAcc = 0;
    this._beatIdx = 0; this._beatAge = 1;
    this._dir = 1;
    this._wheelPat = PAT_SPIN; this._wheelPatPrev = PAT_SPIN; this._patMix = 1;
    this._phraseCount = 0;
    this._alternate = 0; this._alternateTarget = 0;
    this._lowSmooth = 0.5; this._highSmooth = 0.5; this._midSmooth = 0.5;
    this._lastBeatPhase = null; this._lastBarPhase = null; this._lastPhrase = null;
    this._ownBeat = 0; this._beatsSeen = 0; this._barsSincePhrase = 0;
    this._lastDropAt = -1e9; this._dropDarkUntil = -1e9; this._dropFlashAt = -1e9; this._fastUntil = -1e9;
    this._lastPaletteRef = undefined;
    this._mean = 0.4;
  }

  _resetUniforms() {
    const u = this._u;
    u.uTime.value = 0; u.uStep.value = 0; u.uStepAge.value = 1; u.uDir.value = 1; u.uBeatIdx.value = 0; u.uBeatAge.value = 1; u.uBeatPhase.value = 0; u.uBarPhase.value = 0;
    u.uWheelPat.value = 0; u.uWheelPatPrev.value = 0; u.uPatMix.value = 1; u.uGlobal.value = 0.5; u.uDropDark.value = 0; u.uDropFlash.value = 0; u.uPause.value = 0; u.uAlternate.value = 0; u.uMean.value = 0.4;
    u.uOff.value = null; u.uDiff.value = null; u.uIds.value = null; u.uBulbs.value = null; u.uIntens.value = null;
  }

  // Cover fit of the picture in the frame.
  _reframe(wpx, hpx) {
    const frame = wpx / Math.max(1, hpx), img = this._imageAspect;
    const u = this._u;
    if (frame >= img) { const s = img / frame; u.uUvScale.value.set(1, s); u.uUvOffset.value.set(0, (1 - s) / 2); }
    else { const s = frame / img; u.uUvScale.value.set(s, 1); u.uUvOffset.value.set((1 - s) / 2, 0); }
  }

  _applyPalette(palette) {
    const vivid = palette && palette.vividColors && palette.vividColors.length ? palette.vividColors : [0xff2020, 0x2060ff];
    const lin = (hex, sat) => { const c = new THREE.Color(hex); c.getHSL(_hsl); c.setHSL(_hsl.h, Math.max(sat, _hsl.s), 0.55); return [srgbToLinear(c.r), srgbToLinear(c.g), srgbToLinear(c.b)]; };
    const a = lin(vivid[0], COLOR_SAT), b = vivid.length > 1 ? lin(vivid[1], COLOR_SAT) : [1, 1, 1];
    this._u.uColorA.value.set(a[0], a[1], a[2]);
    // the pair: a colour and white, or two colours, as the fair's own strings
    if (this._phraseCount % 2 === 0) this._u.uColorB.value.set(1, 0.92, 0.75); else this._u.uColorB.value.set(b[0], b[1], b[2]);
  }

  _onPhrase() {
    this._phraseCount++;
    // the wheel's next pattern, crossfaded
    if (this._forcedWheel == null) {
      this._wheelPatPrev = this._wheelPat;
      this._wheelPat = WHEEL_PATTERNS[this._phraseCount % WHEEL_PATTERNS.length];
      this._patMix = 0;
    }
    if (this._wheelPat === PAT_SPIN) this._dir = -this._dir;
    this._alternateTarget = (this._phraseCount % 2 === 1) ? 1 : 0;
    this._applyPalette(this._lastPaletteRef);
  }

  _onDrop(beatS) {
    this._lastDropAt = this._time;
    this._dropDarkUntil = this._time + DROP_DARK_BEATS * beatS;
    this._dropFlashAt = this._dropDarkUntil;
    this._fastUntil = this._dropDarkUntil + DROP_FAST_BARS * 4 * beatS;
    this._wheelPatPrev = this._wheelPat; this._wheelPat = PAT_SPIN; this._patMix = 1;
    if (this._pyro) {
      // three shells behind the wheel, over the first bars
      const hub = this._hub, asp = this._imageAspect;
      for (let k = 0; k < 3; k++) {
        const x = (hub[0] - 0.5) * 2 * asp + (k - 1) * 0.35, y = PYRO_GROUND_Y;
        this._pyroQueue.push({ at: this._dropFlashAt + 0.4 + k * 0.9, n: { x: x, y: y, tilt: 0, dx: 0, dy: 1 } });
      }
    }
  }

  update(deltaTime, profile) {
    this._paused = profile.playbackState === 'PAUSED';
    const u = this._u;
    if (this._paused) { u.uPause.value = 1; return; }
    u.uPause.value = 0;
    const dt = Math.min(deltaTime, 0.05);
    this._time += dt;
    const director = profile.director || {};
    const bands = director.bands || null;
    const has = !!(bands && director.hasReliableGrid);
    const kb = 1 - Math.exp(-dt / 0.2);
    this._lowSmooth += ((has ? clamp01(bands.low) : 0.5 + 0.3 * Math.sin(this._time * 1.7)) - this._lowSmooth) * kb;
    this._midSmooth += ((has ? clamp01(bands.mid) : 0.5 + 0.3 * Math.sin(this._time * 1.3 + 2)) - this._midSmooth) * kb;
    this._highSmooth += ((has ? clamp01(bands.high) : 0.5 + 0.3 * Math.sin(this._time * 2.3 + 1)) - this._highSmooth) * kb;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || FALLBACK_BPM;
    const beatS = 60 / Math.max(60, bpm);

    if (profile.artworkPalette !== this._lastPaletteRef) { this._lastPaletteRef = profile.artworkPalette; this._applyPalette(profile.artworkPalette); }

    // --- the grid, or a beat of our own ---------------------------------------------
    let newBeat = false, newBar = false, newPhrase = false;
    let beatPhase = profile.beatPhase, barPhase = profile.barPhase;
    if (beatPhase != null) {
      if (this._lastBeatPhase != null && beatPhase < this._lastBeatPhase - 0.5) newBeat = true;
      if (this._lastBeatPhase == null) newBeat = true;
      this._lastBeatPhase = beatPhase;
      if (barPhase != null) { if (this._lastBarPhase != null && barPhase < this._lastBarPhase - 0.5) newBar = true; this._lastBarPhase = barPhase; }
      else { if (newBeat && (this._beatsSeen % 4) === 0) newBar = true; barPhase = ((this._beatsSeen % 4) + beatPhase) / 4; }
      if (profile.phraseIndex != null && this._lastPhrase != null && profile.phraseIndex !== this._lastPhrase) newPhrase = true;
      if (profile.phraseIndex != null) this._lastPhrase = profile.phraseIndex;
    } else {
      this._ownBeat += dt;
      if (this._ownBeat >= beatS) { this._ownBeat -= beatS; newBeat = true; }
      beatPhase = this._ownBeat / beatS;
      if (newBeat && (this._beatsSeen % 4) === 0) newBar = true;
      barPhase = ((this._beatsSeen % 4) + beatPhase) / 4;
    }
    if (newBeat) { this._beatsSeen++; this._beatIdx++; this._beatAge = 0; }
    if (newBar) { this._barsSincePhrase++; if (!newPhrase && this._barsSincePhrase >= 4) newPhrase = true; }
    if (newPhrase) { this._barsSincePhrase = 0; this._onPhrase(); }
    this._beatAge += dt;

    // --- the drop ---------------------------------------------------------------------------
    const ev = director.nextEvent;
    const dropNow = (ev && ev.type === 'drop' && ev.msUntil != null && ev.msUntil <= 0 && this._time - this._lastDropAt > 4.0)
      || (this._forceDrop && this._time > 2.0 && this._time - this._lastDropAt > 10.0);
    if (dropNow) this._onDrop(beatS);
    const fast = this._time < this._fastUntil;

    // --- the chase notch: quarter beats, eighths after the drop ----------------------------
    const perBeat = fast ? CHASE_FAST_PER_BEAT : CHASE_PER_BEAT;
    const notch = Math.floor(beatPhase * perBeat);
    if (notch !== this._lastNotch || newBeat) { if (this._lastNotch != null) { this._step++; this._stepAge = 0; } this._lastNotch = notch; }
    this._stepAge += dt;
    this._patMix = Math.min(1, this._patMix + dt / 0.6);
    this._alternate += (this._alternateTarget - this._alternate) * (1 - Math.exp(-dt / 0.4));

    // --- the pyro queue ---------------------------------------------------------------------
    if (this._pyro) {
      for (let i = this._pyroQueue.length - 1; i >= 0; i--) { if (this._time >= this._pyroQueue[i].at) { this._pyro.fireShell(this._pyroQueue[i].n); this._pyroQueue.splice(i, 1); } }
      this._pyro.update(dt, this._highSmooth, profile.progress || 0);
    }

    // --- uniforms ------------------------------------------------------------------------------
    u.uTime.value = this._time % 1000;
    u.uStep.value = this._step;
    u.uStepAge.value = this._stepAge;
    u.uDir.value = this._dir;
    u.uBeatIdx.value = this._beatIdx;
    u.uBeatAge.value = this._beatAge;
    u.uBeatPhase.value = beatPhase != null ? beatPhase : 0;
    u.uBarPhase.value = barPhase != null ? barPhase : 0;
    u.uWheelPat.value = this._forcedWheel != null ? this._forcedWheel : this._wheelPat;
    u.uWheelPatPrev.value = this._forcedWheel != null ? this._forcedWheel : this._wheelPatPrev;
    u.uPatMix.value = this._patMix;
    u.uEq.value.set(0.25 + 0.75 * this._lowSmooth, 0.25 + 0.75 * this._midSmooth, 0.25 + 0.75 * this._highSmooth, 0.25 + 0.75 * (0.5 * this._lowSmooth + 0.5 * this._highSmooth));
    u.uTwinkle.value = lerp(TWINKLE_RATE[0], TWINKLE_RATE[1], this._highSmooth);
    u.uGlobal.value = this._lowSmooth;
    u.uDropDark.value = this._time < this._dropDarkUntil ? 1 : 0;
    const sinceFlash = this._time - this._dropFlashAt;
    u.uDropFlash.value = sinceFlash >= 0 ? Math.max(0, 1 - sinceFlash / (1.0 * beatS)) : 0;
    u.uAlternate.value = this._alternate;
    // the mean intensity a far halo follows: a third of the bulbs in a chase, dimmed by the drop
    const mean = (0.33 * (1 - BASE_DIM) + BASE_DIM) * (0.75 + 0.45 * this._lowSmooth) * (1 - u.uDropDark.value);
    this._mean = Math.max(mean, u.uDropFlash.value * DROP_FLASH);
    u.uMean.value = this._solo >= 0 ? 0 : this._mean;
    const t = this._time;
    for (let i = 0; i < BOKEH; i++) {
      const f = u.uFireflies.value[i];
      const on = i < u.uBokeh.value ? 1 : 0;
      f.set((i * 0.618 + t * 0.012 * (1 + 0.3 * i)) % 1, (i * 0.381 + 0.31 + t * (0.007 + 0.003 * i)) % 1, on * 0.045 * this._mean * (0.6 + 0.4 * Math.sin(t * 1.7 + i)), 0);
    }
  }

  get probe() {
    return {
      time: this._time, count: this._count, step: this._step, beatIdx: this._beatIdx, dir: this._dir, wheelPat: this._u.uWheelPat.value, alternate: this._alternate,
      dropDark: this._u.uDropDark.value, dropFlash: this._u.uDropFlash.value, fast: this._time < this._fastUntil, phrases: this._phraseCount, assets: !!this._assets, hub: this._hub, stalls: this._stallCount, tier: this._tier
    };
  }

  dispose() {
    this._scene.remove(this._quad);
    this._quad.geometry.dispose();
    this._bulbQuad.geometry.dispose();
    this._intens.dispose();
    this._bulbsTex.dispose();
    if (this._pyro) { this._pyro.dispose(); this._pyro = null; }
    this._u.uOff.value = null; this._u.uDiff.value = null; this._u.uIds.value = null; this._u.uBulbs.value = null; this._u.uIntens.value = null;
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
const _hsl = { h: 0, s: 0, l: 0 };
